import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useThrottledState } from '~/eds/src/hooks/useThrottledState';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { testHasTextMatch } from '~/eds/src/utils/testHasTextMatch';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { testHasFlag } from '~/permissions/utils/testHasFlag';
import { api } from '~/redux/api/createApi';
import {
  ClauseContent,
  DocumentViewClauses as DocumentViewClause,
} from '~/types/Clause';
import { PilotId } from '~/types/PilotId';

import { ClausesAccordion } from '../../ClausesAccordion/ClausesAccordion';

interface Props {
  docHandlerId?: PilotId;
  featuredClauses?: string[];
  onClauseHighlight?: (clause: ClauseContent) => void;
  search?: string;
}

export const Clauses = ({
  docHandlerId,
  featuredClauses,
  onClauseHighlight,
  search: externalSearch,
}: Props) => {
  const intl = useIntl();
  const [throttledSearchInternal, searchInternal, handleSearchInternal] =
    useThrottledState('', INPUT_DEBOUNCE_MS);
  const hasDocumentDetailsPanelUpdate = useFlag(
    FlagType.DocumentDetailsPanelUpdate,
  );
  const throttledSearch = externalSearch ?? throttledSearchInternal;
  const search = externalSearch ?? searchInternal;
  const [activeClauseId, setActiveClauseId] = React.useState<
    EdsPilotId | undefined
  >();
  const currentUser = useCurrentUser();
  const hasEditClauseFlag = testHasFlag(FeatureFlagType.EditClause)(
    currentUser,
  );
  const endpoint = hasEditClauseFlag
    ? 'getAllClausesGrouped'
    : 'getDocumentDetailClauses';

  const {
    data = [],
    isFetching,
    isError,
  } = api.endpoints[endpoint].useQueryState(
    hasEditClauseFlag
      ? { docId: docHandlerId!, page: 1, pageSize: 100 }
      : ({ documentId: docHandlerId! } as any),
    {
      skip: !docHandlerId,
    },
  );

  const filteredClauses = useMemo(
    () =>
      throttledSearch.length
        ? data?.filter((clause) => testHasTextMatch(clause.name, search))
        : data,
    [data, throttledSearch],
  );

  const loadingContent = {
    isLoading: isFetching,
    message: intl.formatMessage({
      id: 'Clauses.loadingMessageSimple',
      defaultMessage: 'Loading clauses',
      description: 'Loading message for clauses',
    }),
  };

  const placeholderContent = isError
    ? {
        icon: 'field-clause' as const,
        message: intl.formatMessage({
          id: 'Clauses.errorMessage',
          defaultMessage:
            'Something went wrong when retrieving the clauses. Please refresh and load again or contact your admin.',
          description: 'Error message when clauses fail to load',
        }),
      }
    : !data.length
      ? {
          title: intl.formatMessage({
            id: 'Clauses.noClausesFoundTitle',
            defaultMessage: 'No Clauses found.',
            description: 'Title when no clauses are found',
          }),
          message: intl.formatMessage({
            id: 'Clauses.noClausesFoundMessage',
            defaultMessage: 'No clauses were found for this file.',
            description: 'Message when no clauses are found',
          }),
          image: 'empty-no-search',
        }
      : undefined;

  const searchPlaceholderContent =
    search && !filteredClauses.length
      ? {
          title: intl.formatMessage({
            id: 'Clauses.noMatchingClauses',
            defaultMessage: 'No matching clauses',
            description: 'Title when search yields no results',
          }),
          image: 'empty-no-search',
        }
      : undefined;

  const onClauseClick = (clause: ClauseContent) => {
    if (onClauseHighlight) {
      setActiveClauseId(clause.id);
      onClauseHighlight(clause);
    }
  };

  const onToggle = (isExpanded: boolean, clause?: DocumentViewClause) => {
    if (isExpanded && clause && onClauseHighlight) {
      const firstClause = clause.content[0];
      if (firstClause) {
        onClauseClick(firstClause);
      }
    }
  };

  return (
    <ContentContainer
      loadingContent={loadingContent}
      placeholderContent={placeholderContent}
    >
      <Layout h="100%" preset="sections" pt={4}>
        {!hasDocumentDetailsPanelUpdate && (
          <SearchInput
            disabled={isError || isFetching}
            placeholder={intl.formatMessage({
              id: 'Clauses.searchPlaceholder',
              defaultMessage: 'Search clauses',
              description: 'Placeholder text for clause search input',
            })}
            name="search"
            onChange={(text) => handleSearchInternal(text ?? '')}
            value={search}
          />
        )}
        <Box overflowY="auto">
          <ContentContainer placeholderContent={searchPlaceholderContent}>
            <>
              <ClausesAccordion
                activeClauseId={activeClauseId}
                featuredClauses={featuredClauses}
                search={throttledSearch}
                clauses={filteredClauses as any}
                onToggle={onToggle}
                onClauseClick={onClauseClick}
              />
            </>
          </ContentContainer>
        </Box>
      </Layout>
    </ContentContainer>
  );
};

export default Clauses;
